import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { hero, dashboardImage, treeImages } from "../mock";

const Hero = () => {
  const stageRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = stageRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the stage top is near the bottom of viewport (tilted)
      // 1 when it has scrolled up into view (flat/straight)
      const start = vh * 0.9;
      const end = vh * 0.25;
      const raw = (start - rect.top) / (start - end);
      const clamped = Math.min(1, Math.max(0, raw));
      setProgress(clamped);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const rotateX = (1 - progress) * 42; // 42deg tilt -> 0 straight
  const scale = 0.9 + progress * 0.1;
  const translateY = (1 - progress) * 40;

  return (
    <section className="relative overflow-hidden">
      {/* Decorative single continuous Antarctica ice & clouds band */}
      <div
        className="pointer-events-none select-none absolute bottom-0 left-0 right-0 z-0 h-[92vh]"
        style={{
          backgroundImage: `url(${treeImages.ice})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center 22%",
          opacity: 0.95,
          WebkitMaskImage: "linear-gradient(to top, black 58%, rgba(0,0,0,0.45) 80%, transparent 100%)",
          maskImage: "linear-gradient(to top, black 58%, rgba(0,0,0,0.45) 80%, transparent 100%)",
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1200px] px-6 pt-36 pb-8 text-center">
        <h1 className="font-serif text-[#16263F] leading-[0.95] tracking-tight">
          <span className="block text-[6vw] md:text-[4.25rem]">{hero.titleLine1}</span>
          <span className="block text-[6vw] md:text-[4.25rem]">
            {hero.titleLine2Normal}
            <span className="italic">{hero.titleLine2Italic}</span>
          </span>
        </h1>

        <p className="mx-auto mt-6 max-w-xl text-[17px] leading-7 text-slate-400">
          {hero.subtitle[0]}
          <br />
          {hero.subtitle[1]}
        </p>

        <div className="mt-12 flex items-center justify-center gap-4">
          <button className="rounded-2xl bg-[#5BA4FB] px-9 py-4 text-[17px] font-semibold text-white shadow-[0_12px_30px_rgba(91,164,251,0.45)] hover:bg-[#7AB5FC] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200">
            {hero.primaryCta}
          </button>
          <button className="group flex items-center gap-3 rounded-2xl bg-white/80 pl-7 pr-2 py-2 text-[17px] font-semibold text-[#16263F] shadow-[0_10px_30px_rgba(0,0,0,0.07)] border border-white/70 backdrop-blur hover:-translate-y-0.5 transition-all duration-200">
            {hero.secondaryCta}
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#16263F] text-white group-hover:bg-[#5BA4FB] group-hover:text-white transition-colors duration-200">
              <Play className="h-4 w-4 fill-current" />
            </span>
          </button>
        </div>
      </div>

      {/* 3D scroll-interactive dashboard */}
      <div
        ref={stageRef}
        className="relative z-10 mx-auto max-w-[1150px] px-6"
        style={{ perspective: "1600px" }}
      >
        <div
          className="rounded-[28px] overflow-hidden border border-white/70 bg-white shadow-[0_40px_90px_rgba(20,40,70,0.18)] will-change-transform"
          style={{
            transform: `rotateX(${rotateX}deg) scale(${scale}) translateY(${translateY}px)`,
            transformOrigin: "center top",
            transition: "transform 0.08s linear",
          }}
        >
          <img
            src={dashboardImage}
            alt="OpenPenguin Voice dashboard"
            className="w-full block"
          />
        </div>
      </div>

      {/* spacer so forest + dashboard end with breathing room before the fade */}
      <div className="h-40" />

      {/* Beige bottom fade so the ice melts smoothly into the next section */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 z-[5] h-72"
        style={{
          background:
            "linear-gradient(to bottom, rgba(237,241,248,0) 0%, rgba(237,241,248,0.7) 55%, #EDF1F8 100%)",
        }}
      />
    </section>
  );
};

export default Hero;
